import { useEffect, useState } from "react";
import { IconExternalLink } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { StatusText } from "@/components/dashboard/dashboard-card";
import { AsideRow, Field } from "@/components/settings/settings-kit";
import { PLACEMENT_LABEL } from "@/components/content-programs/media-tile";
import { SubmissionDialog } from "@/components/content-programs/submission-dialog";
import { type ContentSubmissionRow, isRunningAd } from "@/services/contentProgram.service";
import type { MetaAspectRatio } from "@/config/contentProgramUpload";
import { formatAmount, formatDate } from "@/utils/formatters";
import { affiliateDisplayName, affiliateInitials, submissionStatus } from "./submission-status";

export function canModerateSubmission(row: ContentSubmissionRow | null): boolean {
  if (!row) return false;
  if (isRunningAd(row)) return false;
  return row.status === "PENDING" || row.status === "CHANGE_REQUESTED" || row.status === "APPROVED";
}

type Step = "REJECT" | "REQUEST_CHANGE" | null;

interface SubmissionReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submission: ContentSubmissionRow | null;
  programTitle: string;
  ratioLabel?: MetaAspectRatio | null;
  isBusy: boolean;
  onApprove: (contentId: string) => void;
  onReject: (contentId: string, message?: string) => void;
  onRequestChange: (contentId: string, message: string) => void;
}

/**
 * The brand's review: the creative on one side, the facts and the decision
 * on the other. Approve is the one primary action; asking for changes or
 * rejecting swaps the facts for a note to the affiliate.
 */
export function SubmissionReviewModal({
  open,
  onOpenChange,
  submission,
  programTitle,
  ratioLabel,
  isBusy,
  onApprove,
  onReject,
  onRequestChange,
}: SubmissionReviewModalProps) {
  const [step, setStep] = useState<Step>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setStep(null);
      setMessage("");
    }
  }, [open, submission?.id]);

  if (!submission) return null;

  const mod = canModerateSubmission(submission);
  const canApprove = mod && (submission.status === "PENDING" || submission.status === "CHANGE_REQUESTED");
  const isVideo = submission.mediaType === "VIDEO";
  const title = submission.title?.trim() || "Untitled";
  const status = submissionStatus(submission.status);
  const stripScheme = (url: string) => url.replace(/^https?:\/\//, "");

  const send = () => {
    if (step === "REQUEST_CHANGE") {
      if (!message.trim()) return;
      onRequestChange(submission.id, message.trim());
    } else if (step === "REJECT") {
      onReject(submission.id, message.trim() || undefined);
    }
  };

  return (
    <SubmissionDialog
      open={open}
      onOpenChange={onOpenChange}
      srTitle={`Review ${title}`}
      mediaUrl={submission.mediaUrl}
      mediaType={isVideo ? "VIDEO" : "IMAGE"}
      ratioLabel={ratioLabel}
      header={
        <>
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
            {affiliateInitials(submission)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-foreground">{title}</div>
            <div className="truncate text-xs text-muted-foreground">
              {affiliateDisplayName(submission)}
              {submission.creatorAffiliate?.username ? ` @${submission.creatorAffiliate.username}` : ""}
            </div>
          </div>
        </>
      }
      footer={
        mod ? (
          step ? (
            <>
              <DialogTextButton onClick={() => setStep(null)} disabled={isBusy}>
                Back
              </DialogTextButton>
              <Button
                type="button"
                variant={step === "REJECT" ? "destructive" : "default"}
                onClick={send}
                disabled={isBusy || (step === "REQUEST_CHANGE" && !message.trim())}
              >
                {step === "REQUEST_CHANGE" ? "Send request" : "Reject"}
              </Button>
            </>
          ) : (
            <>
              <DialogTextButton onClick={() => setStep("REJECT")} disabled={isBusy} className="hover:text-destructive">
                Reject
              </DialogTextButton>
              <DialogTextButton onClick={() => setStep("REQUEST_CHANGE")} disabled={isBusy}>
                Request changes
              </DialogTextButton>
              {canApprove && (
                <Button type="button" onClick={() => onApprove(submission.id)} disabled={isBusy}>
                  Approve
                </Button>
              )}
            </>
          )
        ) : undefined
      }
    >
      {step ? (
        <div className="space-y-3 py-1">
          <Field
            label={step === "REQUEST_CHANGE" ? "What should change" : "Reason (optional)"}
            htmlFor="review-message"
            help={
              step === "REQUEST_CHANGE"
                ? "The affiliate sees this note and can resubmit"
                : "The affiliate sees this note and can submit something new"
            }
          >
            <Textarea
              id="review-message"
              rows={5}
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={step === "REQUEST_CHANGE" ? "Show the product in the first 3 seconds" : "Off brand for this program"}
            />
          </Field>
        </div>
      ) : (
        <>
          <AsideRow label="Status">
            <StatusText status={submission.status} label={status.label} />
          </AsideRow>
          <AsideRow label="Program">{programTitle}</AsideRow>
          <AsideRow label="Format">{isVideo ? "Video" : "Image"}</AsideRow>
          <AsideRow label="Size">
            {ratioLabel ? `${ratioLabel}, ${PLACEMENT_LABEL[ratioLabel].toLowerCase()}` : "Unknown"}
          </AsideRow>
          <AsideRow label="Products">
            {submission.products?.length
              ? submission.products.length === 1
                ? submission.products[0].title
                : `${submission.products.length} products`
              : "None tagged"}
          </AsideRow>
          {submission.tiktokAdRequest && (
            <AsideRow label="Pay">
              {[
                Number(submission.tiktokAdRequest.flatFee ?? 0) > 0 ? `${formatAmount(Number(submission.tiktokAdRequest.flatFee))} fee on approval` : null,
                submission.tiktokAdRequest.commissionRate !== null ? `${submission.tiktokAdRequest.commissionRate}% on ad sales` : null,
              ]
                .filter(Boolean)
                .join(" + ") || "Program rate"}
            </AsideRow>
          )}
          <AsideRow label="Submitted">{formatDate(submission.createdAt)}</AsideRow>
          {submission.updatedAt !== submission.createdAt && (
            <AsideRow label="Updated">{formatDate(submission.updatedAt)}</AsideRow>
          )}
          {submission.destinationUrl && (
            <AsideRow label="Links to">
              <a
                href={submission.destinationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 underline-offset-4 hover:underline"
              >
                <span className="truncate">{stripScheme(submission.destinationUrl)}</span>
                <IconExternalLink size={12} stroke={1.75} className="shrink-0" aria-hidden />
              </a>
            </AsideRow>
          )}

          {submission.products && submission.products.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {submission.products.map((product) => (
                <span
                  key={product.id}
                  className="inline-flex items-center gap-2 rounded-md border border-border py-1 pl-1 pr-2.5 text-xs text-foreground"
                >
                  <img src={product.image ?? "/placeholder-product.png"} alt="" className="size-6 rounded bg-muted object-cover" />
                  <span className="max-w-[10rem] truncate">{product.title}</span>
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 space-y-2">
            {submission.tiktokAdRequest && (
              <DialogNotice tone="success">
                Answers your TikTok ad request
                {submission.tiktokAdRequest.notes ? `: ${submission.tiktokAdRequest.notes}` : ""}
                {submission.launchedAd?.status === "DRAFT" ? ". A draft ad is ready under Launch once approved." : ""}
              </DialogNotice>
            )}
            {submission.reviewMessage && (
              <DialogNotice tone="warning">Your last note: {submission.reviewMessage}</DialogNotice>
            )}
            {isRunningAd(submission) && (
              <DialogNotice tone="success">This creative runs as a Meta ad. Its review is closed.</DialogNotice>
            )}
            {!mod && submission.status === "REJECTED" && (
              <p className="text-xs text-muted-foreground">Rejected. The affiliate can submit something new.</p>
            )}
          </div>
        </>
      )}
    </SubmissionDialog>
  );
}
